import React from 'react';
import { T, LOGO_CONFIG } from '../../utils/theme';
import { Scissors, TrendingUp } from 'lucide-react';

export const BrandMark = ({ size = 22 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
    <div style={{
      width: size, height: size, borderRadius: size * 0.28,
      background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <img src={LOGO_CONFIG.url} alt="Barbx"
        style={{ width: size * 0.62, height: size * 0.62, objectFit: 'contain', filter: 'invert(1)' }}
        onError={(e) => { e.target.style.display = 'none'; }} />
    </div>
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: T.label2, textTransform: 'uppercase' }}>Barbx</span>
  </div>
);

export const Logo = ({ className = "w-10 h-10" }) => (
  <div className="flex items-center gap-2">
    <div className="w-full flex justify-center">
      <img src={LOGO_CONFIG.url} alt={LOGO_CONFIG.alt}
        style={{ maxWidth: '72px', width: '100%', height: 'auto', objectFit: 'contain' }}
        onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
      <div className={`rounded-full items-center justify-center ${className}`}
        style={{ display: 'none', backgroundColor: T.accent }}>
        <Scissors className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

export const SolidCard = ({ children, style = {}, padding = 16, radius = 20, onClick, className = '' }) => (
  <div onClick={onClick} className={className} style={{
    borderRadius: radius, padding, background: T.surface,
    boxShadow: T.shadow, border: `0.5px solid ${T.glassRing}`, ...style,
  }}>{children}</div>
);

export const GlassCard = ({ children, style = {}, padding = 16, radius = 20, strong = false }) => (
  <div style={{
    position: 'relative', borderRadius: radius, padding,
    background: strong ? T.glassStrong : T.glass,
    backdropFilter: 'blur(30px) saturate(180%)', WebkitBackdropFilter: 'blur(30px) saturate(180%)',
    border: `0.5px solid ${T.glassBorder}`, boxShadow: T.shadow, ...style,
  }}>{children}</div>
);

export const Segmented = ({ items, value, onChange, size = 'md' }) => {
  const pad = size === 'sm' ? '6px 12px' : '8px 14px';
  const fs = size === 'sm' ? 13 : 14;
  return (
    <div className="segmented-control">
      {items.map((item) => (
        <button key={item.value} onClick={() => onChange && onChange(item.value)}
          className={`segmented-btn ${item.value === value ? 'active' : ''}`}
          style={{ padding: pad, fontSize: fs }}>
          {item.label}
        </button>
      ))}
    </div>
  );
};

export const LargeTitle = ({ title, subtitle, trailing, showBrand = true }) => (
  <div className="large-title-wrapper" style={{ padding: '20px 20px 12px' }}>
    {showBrand && <div style={{ marginBottom: 12 }}><BrandMark /></div>}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="large-title">{title}</div>
        {subtitle && <div style={{ fontSize: 15, color: T.label2, marginTop: 4, letterSpacing: '-0.01em' }}>{subtitle}</div>}
      </div>
      {trailing && <div>{trailing}</div>}
    </div>
  </div>
);

export const SectionHeader = ({ title, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 4px 10px' }}>
    <div className="section-title">{title}</div>
    {action && (
      <button onClick={action.onClick} style={{
        border: 'none', background: 'transparent', color: T.accent,
        fontFamily: 'inherit', fontSize: 15, fontWeight: 500, cursor: 'pointer', padding: 4,
      }}>{action.label}</button>
    )}
  </div>
);

export const Avatar = ({ name = '', size = 40, tint }) => {
  const initials = (name || '?').split(' ').slice(0, 2).map(p => (p[0] || '').toUpperCase()).join('');
  const palette = [T.accent, T.warning, T.purple, T.success, T.pink, T.teal, T.danger, T.indigo];
  const idx = [...(name || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  const bg = tint || palette[idx];
  return (
    <div className="avatar" style={{
      width: size, height: size, borderRadius: size / 2,
      background: `linear-gradient(135deg, ${bg} 0%, ${bg}CC 100%)`,
      fontSize: size * 0.38, letterSpacing: '-0.01em', boxShadow: `0 2px 6px ${bg}55`,
    }}>{initials}</div>
  );
};

export const KpiTile = ({ icon: IconComp, tint, label, value, sub }) => (
  <SolidCard padding={14} radius={18}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div className="kpi-icon" style={{ background: tint + '22', color: tint }}>
        <IconComp size={17} color={tint} strokeWidth={2} />
      </div>
      <TrendingUp size={14} color={T.label3} />
    </div>
    <div style={{ fontSize: 12, fontWeight: 500, color: T.label2, marginTop: 10, letterSpacing: '-0.01em' }}>{label}</div>
    <div className="tnum" style={{ fontSize: 22, fontWeight: 700, color: T.label, letterSpacing: '-0.025em', marginTop: 2 }}>{value}</div>
    <div style={{ fontSize: 11, color: T.label3, marginTop: 1 }}>{sub}</div>
  </SolidCard>
);

export const Sparkline = ({ data = [], color = T.accent, height = 38 }) => {
  if (!data.length) return null;
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const w = 100, h = height;
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => [i * step, h - ((v - min) / range) * (h - 4) - 2]);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  const id = `sp-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const HeroCard = ({ gradient, children, shadowColor }) => (
  <div style={{
    borderRadius: 24, overflow: 'hidden', position: 'relative',
    background: gradient, padding: 20, color: '#fff',
    boxShadow: `0 10px 30px ${shadowColor || 'rgba(10,132,255,0.35)'}`,
  }}>
    <div style={{
      position: 'absolute', inset: 0,
      background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 50%)',
      pointerEvents: 'none',
    }}/>
    <div style={{ position: 'relative' }}>{children}</div>
  </div>
);

export const StatusBadge = ({ status, label }) => {
  const colors = {
    paga: { bg: 'rgba(48,209,88,0.14)', color: T.success },
    pendente: { bg: 'rgba(255,159,10,0.14)', color: T.warning },
    live: { bg: 'rgba(48,209,88,0.14)', color: T.success },
    danger: { bg: 'rgba(255,59,48,0.14)', color: T.danger },
  };
  const c = colors[status] || colors.pendente;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: c.color,
      background: c.bg, padding: '2px 7px', borderRadius: 999, letterSpacing: 0,
    }}>{label}</span>
  );
};
